import React, { useRef, useState } from "react";

const pad2 = (text) => (text.length === 1 ? "0" + text : text);

// Reads typed digits as an hour. A first digit of 3-9 can only be a one-digit
// hour (9 -> 09); 24-29 cannot be an hour, so "245" is read as 02:45.
// Returns the hour text, the digits left over for the minutes, and whether the
// hour is complete (which is when focus should move on to the minutes).
export const parseHourDigits = (digits) => {
  if (!digits) return { text: "", rest: "", done: false };
  if (digits[0] >= "3") return { text: "0" + digits[0], rest: digits.slice(1), done: true };
  if (digits.length === 1) return { text: digits, rest: "", done: false };
  if (Number(digits.slice(0, 2)) <= 23) {
    return { text: digits.slice(0, 2), rest: digits.slice(2), done: true };
  }
  return { text: "0" + digits[0], rest: digits.slice(1), done: true };
};

// A first digit of 6-9 can only be a one-digit minute (7 -> 07); otherwise two
// digits are 00-59 by construction.
export const parseMinuteDigits = (digits) => {
  if (!digits) return { text: "", done: false };
  if (digits[0] >= "6") return { text: "0" + digits[0], done: true };
  if (digits.length === 1) return { text: digits, done: false };
  return { text: digits.slice(0, 2), done: true };
};

export const toInt = (value, fallback, min, max) => {
  const n = parseInt(value, 10);
  if (isNaN(n)) return fallback;
  return Math.min(Math.max(n, min), max);
};

const Svg = ({ size = 20, strokeWidth = 1.8, children, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    {children}
  </svg>
);

export const CalendarIcon = () => (
  <Svg className="pill-icon">
    <rect x="3.5" y="5" width="17" height="15.5" rx="3"></rect>
    <path d="M3.5 10h17M8 3v4M16 3v4"></path>
  </Svg>
);

export const ClockIcon = () => (
  <Svg className="pill-icon">
    <circle cx="12" cy="12" r="8.5"></circle>
    <path d="M12 7.5V12l3 2"></path>
  </Svg>
);

export const UserIcon = () => (
  <Svg>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </Svg>
);

export const BagIcon = () => (
  <Svg>
    <rect x="5" y="7" width="14" height="13" rx="2"></rect>
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
    <path d="M9.5 11v5M14.5 11v5"></path>
  </Svg>
);

export const MinusIcon = () => (
  <Svg size={18} strokeWidth={2}>
    <path d="M5 12h14"></path>
  </Svg>
);

export const PlusIcon = () => (
  <Svg size={18} strokeWidth={2}>
    <path d="M12 5v14M5 12h14"></path>
  </Svg>
);

export const CheckIcon = () => (
  <Svg size={13} strokeWidth={3}>
    <path d="M5 12.5l4.5 4.5L19 7.5"></path>
  </Svg>
);

export const PhoneIcon = () => (
  <Svg size={22} strokeWidth={1.9}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </Svg>
);

export const VehicleIcon = ({ type }) => {
  const common = {
    viewBox: "0 0 64 32",
    width: 52,
    height: 26,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    className: "veh-icon",
  };
  if (type === "berline") {
    return (
      <svg {...common}>
        <path d="M5 23v-4a3 3 0 0 1 3-3h5l7-8h18l8 8h9a4 4 0 0 1 4 4v3"></path>
        <path d="M5 23h6M23 23h18M53 23h6"></path>
        <circle cx="17" cy="23" r="5"></circle>
        <circle cx="47" cy="23" r="5"></circle>
        <path d="M18.5 16l3.6-4.5H29V16z"></path>
        <path d="M32 11.5h4.6L41 16h-9z"></path>
      </svg>
    );
  }
  if (type === "minibus") {
    return (
      <svg {...common}>
        <path d="M4 23V9a3 3 0 0 1 3-3h40l9 10v7"></path>
        <path d="M4 23h7M23 23h18M53 23h7"></path>
        <circle cx="17" cy="23" r="5"></circle>
        <circle cx="47" cy="23" r="5"></circle>
        <path d="M9 10h8v6H9zM20 10h8v6h-8zM31 10h8v6h-8z"></path>
        <path d="M43 10l6 6h-6z"></path>
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5 23V10a4 4 0 0 1 4-4h28c1.3 0 2.5.6 3.3 1.6L47 16h8a4 4 0 0 1 4 4v3"></path>
      <path d="M5 23h6M23 23h18M53 23h6"></path>
      <circle cx="17" cy="23" r="5"></circle>
      <circle cx="47" cy="23" r="5"></circle>
      <path d="M11 11h9v6h-9z"></path>
      <path d="M24 11h12.5l5 6H24z"></path>
    </svg>
  );
};

export const Stepper = ({ icon, label, value, min, max, lessLabel, moreLabel, onChange }) => (
  <div className="step-row">
    <span className="step-icon">{icon}</span>
    <span className="step-label">{label}</span>
    <div className="step-ctrl">
      <button
        type="button"
        className="step-btn"
        aria-label={lessLabel}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        <MinusIcon />
      </button>
      <span className="step-value" aria-live="polite">{value}</span>
      <button
        type="button"
        className="step-btn"
        aria-label={moreLabel}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        <PlusIcon />
      </button>
    </div>
  </div>
);

// Keyboard time entry: type 1045 (or 945, 10:45, 10h45) without leaving the field.
// The two boxes keep a local draft; the quote state always receives a padded
// two-digit value (or "" when empty, which the required attribute rejects on submit).
export const TimeInput = ({ hourName, minuteName, hourValue, minuteValue, minuteLabel, onCommit }) => {
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const [hourText, setHourText] = useState(hourValue || "");
  const [minuteText, setMinuteText] = useState(minuteValue || "");
  // Latest drafts, readable synchronously: moving focus fires blur before React re-renders.
  const hourDraft = useRef(hourValue || "");
  const minuteDraft = useRef(minuteValue || "");

  const commitHour = (text) => {
    hourDraft.current = text;
    setHourText(text);
    onCommit(hourName, text ? pad2(text) : "");
  };
  const commitMinute = (text) => {
    minuteDraft.current = text;
    setMinuteText(text);
    onCommit(minuteName, text ? pad2(text) : "");
  };
  const focusField = (ref) => {
    if (ref.current) {
      ref.current.focus();
      ref.current.select();
    }
  };

  const applyMinuteDigits = (digits) => commitMinute(parseMinuteDigits(digits).text);
  const applyHourDigits = (digits) => {
    const hour = parseHourDigits(digits);
    commitHour(hour.text);
    if (hour.done) {
      focusField(minuteRef);
      if (hour.rest) applyMinuteDigits(hour.rest);
    }
  };

  const onHourKeyDown = (event) => {
    if ([":", "h", "H", ".", ",", " "].includes(event.key) && hourDraft.current) {
      event.preventDefault();
      commitHour(pad2(hourDraft.current));
      focusField(minuteRef);
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const current = parseInt(hourDraft.current || "0", 10);
      commitHour(pad2(String((current + (event.key === "ArrowUp" ? 1 : 23)) % 24)));
    }
  };
  const onMinuteKeyDown = (event) => {
    if (event.key === "Backspace" && !minuteDraft.current) {
      event.preventDefault();
      focusField(hourRef);
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const current = parseInt(minuteDraft.current || "0", 10);
      commitMinute(pad2(String((current + (event.key === "ArrowUp" ? 1 : 59)) % 60)));
    }
  };

  return (
    <div className="pill-time">
      <input
        ref={hourRef}
        id={hourName}
        name={hourName}
        className="time-seg"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="HH"
        required
        value={hourText}
        onChange={(event) => {
          const raw = event.target.value;
          const digits = raw.replace(/\D/g, "");
          if (raw && !digits) return;
          applyHourDigits(digits);
        }}
        onKeyDown={onHourKeyDown}
        onFocus={(event) => event.target.select()}
        onBlur={() => hourDraft.current.length === 1 && commitHour(pad2(hourDraft.current))}
      />
      <span aria-hidden="true">:</span>
      <input
        ref={minuteRef}
        id={minuteName}
        name={minuteName}
        className="time-seg"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="MM"
        aria-label={minuteLabel}
        required
        value={minuteText}
        onChange={(event) => {
          const raw = event.target.value;
          const digits = raw.replace(/\D/g, "");
          if (raw && !digits) return;
          applyMinuteDigits(digits);
        }}
        onKeyDown={onMinuteKeyDown}
        onFocus={(event) => event.target.select()}
        onBlur={() => minuteDraft.current.length === 1 && commitMinute(pad2(minuteDraft.current))}
      />
    </div>
  );
};
